import React, { useState, useEffect, useRef, useCallback } from "react";
import styled from "styled-components";
import axios from "axios";
import { FiPenTool } from "react-icons/fi"; // Import pen tool icon
import { useParams } from "react-router-dom"; // Import useParams from react-router-dom
import AddPostModal from "./AddPostModal";
import Post from "./Post";
import LoginModal from "./LoginModal";
import UpdateUserModal from "./UpdateUserModal";
import {
  NotificationContainer,
  NotificationManager,
} from "react-notifications";
import "react-notifications/lib/notifications.css";

const Main2 = ({activeCategory, setActiveCategory}) => {
  const { postId } = useParams(); // Get postId from URL params
  const [showModal, setShowModal] = useState(false);
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showUpdateUserModal, setShowUpdateUserModal] = useState(false);
  const [postCache, setPostCache] = useState({});
  const observer = useRef();

  useEffect(() => {
    const token = localStorage.getItem("token");
    const userKey = localStorage.getItem("user_key");
    setIsAuthenticated(token && userKey);
  }, []);

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if (activeCategory) {
      if (postCache[activeCategory]) {
        // If posts for the active category are already cached, use them
        setPosts(postCache[activeCategory].posts);
        setCurrentPage(postCache[activeCategory].currentPage);
        setLastPage(postCache[activeCategory].lastPage);
      } else {
        // Otherwise, fetch posts from the API
        setPosts([]);
        setCurrentPage(1);
        fetchPosts(1, activeCategory);
      }
    }
  }, [activeCategory]);

  useEffect(() => {
    if (currentPage > 1) {
      fetchPosts(currentPage, activeCategory);
    }
  }, [currentPage]);

  useEffect(() => {
    // Fetch the specific post if postId is present in the URL
    if (postId) {
      fetchSpecificPost(postId);
    }
  }, [postId]);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/community-categories`
      );
      console.log(response.data.data);
      if (response.data && response.data.data)
        setCategories(response.data.data);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
    setLoading(false);
  };

  const fetchPosts = async (page, category) => {
    setIsLoading(true);
    const key = localStorage.getItem("user_key");
    let API_URL;
    if (category === "my-posts")
      API_URL = `${process.env.REACT_APP_API_BASE_URL}/community-user/${key}/posts`;
    else if (category === "all-posts")
      API_URL = `${process.env.REACT_APP_API_BASE_URL}/community-posts`;
    else
      API_URL = `${process.env.REACT_APP_API_BASE_URL}/community-posts/category/${category}`;
    setLoading(true);
    try {
      const response = await axios.get(API_URL, {
        params: {
          page,
        },
      });
      if (response.data && response.data.data && response.data.data.data) {
        const newPosts = response.data.data.data;
        setPosts((prevPosts) => [...prevPosts, ...newPosts]);
        setLastPage(response.data.data.last_page);

        // Update the post cache
        setPostCache((prevCache) => ({
          ...prevCache,
          [category]: {
            posts: [...(prevCache[category]?.posts || []), ...newPosts],
            currentPage: page,
            lastPage: response.data.data.last_page,
          },
        }));
      }
    } catch (error) {
      console.error("Error fetching posts:", error);
    } finally {
      setIsLoading(false);
    }
    setLoading(false);
  };

  const fetchSpecificPost = async (postId) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/community-posts/${postId}/details`
      );
      if (response.data && response.data.data) {
        const specificPost = response.data.data;
        // Add the specific post at the beginning of the posts array
        setPosts((prevPosts) => [specificPost, ...prevPosts]);
      }
    } catch (error) {
      console.error("Error fetching specific post:", error);
    }
  };

  const handleAddPost = (post) => {
    setPosts([post, ...posts]);
    setPostCache((prevCache) => ({
      ...prevCache,
      [activeCategory]: {
        ...prevCache[activeCategory],
        posts: [post, ...(prevCache[activeCategory]?.posts || [])],
      },
    }));
  };

  const lastPostRef = useCallback(
    (node) => {
      if (isLoading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && currentPage < lastPage) {
          setCurrentPage((prevPage) => prevPage + 1);
        }
      });
      if (node) observer.current.observe(node);
    },
    [isLoading, currentPage, lastPage]
  );

  const handleAddPostClick = () => {
    if (isAuthenticated) {
      setShowModal(true);
    } else {
      setShowLoginModal(true);
    }
  };

  const handleLoginSuccess = (token, userKey) => {
    setIsAuthenticated(true);
    localStorage.setItem("token", token);
    localStorage.setItem("user_key", userKey);
    setShowLoginModal(false);
  };

  const handleUpdateUsername = (newUsername) => {
    NotificationManager.success("User updated successfully!", "Success");
    // console.log("Username updated to:", newUsername);
  };

  const handlePostDelete = (postKey) => {
    setPosts((prevPosts) => prevPosts.filter((post) => post.post_key !== postKey));
    setPostCache((prevCache) => {
      const updatedCache = { ...prevCache };
      if (updatedCache[activeCategory]) {
        updatedCache[activeCategory].posts = updatedCache[activeCategory].posts.filter(
          (post) => post.post_key !== postKey
        );
      }
      return updatedCache;
    });
  };

  return (
    <Container>
      {loading && (
        <div className="loader-container">
          <div className="loader"></div>
        </div>
      )}
      <Wrapper>
        <CategoriesContainer>
          {activeCategory === "my-posts" && (
            <CategoryCard
              active={activeCategory === "my-posts"}
              onClick={() => setActiveCategory("my-posts")}
            >
              <span>My Posts</span>
            </CategoryCard>
          )}
          <CategoryCard
            active={activeCategory === "all-posts"}
            onClick={() => setActiveCategory("all-posts")}
          >
            <span>All</span>
          </CategoryCard>
          {categories.map((category) => (
            <CategoryCard
              key={category.category_key}
              active={activeCategory === category.category_key}
              onClick={() => setActiveCategory(category.category_key)}
            >
              <CategoryIcon>{category.icon}</CategoryIcon>
              <span>{category.category_name}</span>
            </CategoryCard>
          ))}
        </CategoriesContainer>
        <AddPostButton onClick={handleAddPostClick}>
          <FiPenTool /> Add post
        </AddPostButton>
        {!isLoading && posts.length === 0 ? (
          <NoPostsMessage>
            No posts available. Create a post to see posts.
          </NoPostsMessage>
        ) : (
          posts.map((post, index) => {
            if (posts.length === index + 1) {
              return (
                <div ref={lastPostRef} key={post.post_key}>
                  <Post
                    userName={post.user_name || "Unknown User"}
                    postTime={post.created_at || "Unknown Time"}
                    content={post.post_content || ""}
                    images={post.image_path ? [post.image_path] : []}
                    postKey={post.post_key}
                    profileImage={post.profile_image}
                    totalComments={post.total_comments}
                    totalReactions={post.total_reactions}
                    setShowLoginModal={setShowLoginModal}
                    activeCategory={activeCategory}
                    onDeletePost={handlePostDelete}
                  />
                </div>
              );
            } else {
              return (
                <Post
                  key={post.post_key}
                  userName={post.user_name || "Unknown User"}
                  postTime={post.created_at || "Unknown Time"}
                  content={post.post_content || ""}
                  images={post.image_path ? [post.image_path] : []}
                  postKey={post.post_key}
                  profileImage={post.profile_image}
                  totalComments={post.total_comments}
                  totalReactions={post.total_reactions}
                  setShowLoginModal={setShowLoginModal}
                  activeCategory={activeCategory}
                  onDeletePost={handlePostDelete}
                />
              );
            }
          })
        )}
        {isLoading && <p>Loading...</p>}
        {showModal && (
          <AddPostModal
            show={showModal}
            onClose={() => setShowModal(false)}
            onSubmit={handleAddPost}
            activeCategory={activeCategory}
            setActiveCategory={setActiveCategory}
          />
        )}
        {showLoginModal && (
          <LoginModal
            onClose={() => setShowLoginModal(false)}
            onLoginSuccess={handleLoginSuccess}
            onOtpVerificationSuccess={() => setShowUpdateUserModal(true)}
          />
        )}
        <UpdateUserModal
          show={showUpdateUserModal}
          onClose={() => setShowUpdateUserModal(false)}
          onSubmit={handleUpdateUsername}
        />
      </Wrapper>
      <NotificationContainer />
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: #F1F1F1;
  padding-top: 20px;
  padding-bottom: 20px;
  width: 90%;
  box-shadow: 5px 5px 15px rgba(0, 0, 0, 0.1),
    -5px -5px 15px rgba(255, 255, 255, 1);

  @media (max-width: 767px) {
    width: 100%;
  }
`;

const CategoriesContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  width: 95%;
  padding: 10px;
  background-color: #F1F1F1;
  border-radius: 8px;
  box-shadow: 5px 5px 15px rgba(0, 0, 0, 0.1),
    -5px -5px 15px rgba(255, 255, 255, 1);
  margin-bottom: 20px;
  justify-content: center;

  @media (max-width: 767px) {
    padding: 10px;
    width: 90%;
  }
`;

const CategoryCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 20px;
  margin: 5px;
  background-color: ${(props) => (props.active ? "#E75B82" : "#F1F1F1")};
  color: ${(props) => (props.active ? "#fff" : "inherit")};
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.3s ease;
  box-shadow: ${(props) =>
    props.active ? null : "4px 4px 8px #a3b1c6, -4px -4px 8px #ffffff;"};

  &:hover {
    background-color: ${(props) => (props.active ? "#E75B82" : "#e0e0e0")};
  }
`;

const CategoryIcon = styled.span`
  margin-right: 8px;
  font-size: 18px;
`;

const AddPostButton = styled.button`
  position: fixed;
  bottom: 20px;
  right: 20px;
  background-color: #e75b82;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 10px;
  font-size: 16px;
  display: flex;
  align-items: center;
  cursor: pointer;
  box-shadow: 5px 5px 15px rgba(0, 0, 0, 0.1),
    -5px -5px 15px rgba(255, 255, 255, 1);

  svg {
    margin-right: 8px;
  }
`;

const NoPostsMessage = styled.p`
  font-size: 18px;
  font-weight: bold;
  color: #333;
  margin-top: 20px;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.1);
`;

export default Main2;